import PdfIcon from "@mui/icons-material/PictureAsPdf";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useState } from "react";
import dayjs from "../../dayjs";
import AsyncLoadingButton from "../asyncLoadingButton/AsyncLoadingButton";
import MultiOptionDownloadButton from "./MultiOptionDownloadButton";
import SingleOptionDownloadButton from "./SingleOptionDownloadButton";
import YearPicker from "./YearPicker";
import { useDownloadFile } from "./useDownloadFile";

interface YearDialogProps {
  readonly open: boolean;
  readonly maxYear?: number;
  readonly onSelect: (year: number) => Promise<void>;
  readonly onCancel: () => void;
}

export const YearDialog: FC<YearDialogProps> = ({ open, maxYear, onSelect, onCancel }) => {
  const [year, setYear] = useState<number | null>(dayjs().year());
  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>Wybierz rok</DialogTitle>
      <DialogContent>
        <YearPicker value={year} minYear={dayjs().subtract(2, "years").year()} maxYear={maxYear} onChange={setYear} />
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={onCancel}>
          Anuluj
        </Button>
        <AsyncLoadingButton
          disabled={year === null}
          onClick={async () => {
            if (year) {
              await onSelect(year);
            }
          }}
        >
          Potwierdź
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

interface DownloadOption {
  name: string;
  apiDefinition: (year: number) => Promise<Blob>;
}

interface Props {
  readonly buttonText: string;
  readonly startIcon?: ReactNode;
  readonly options: DownloadOption[];
  readonly useDynamicPrices: boolean;
  readonly onError: (error: unknown) => void;
  readonly filename: (year: number) => string;
}

const DownloadPdfButtonWithYear: FC<Props> = ({
  buttonText,
  startIcon,
  options,
  useDynamicPrices,
  onError,
  filename,
}) => {
  const [optionToDownload, setOptionToDownload] = useState<string | null>(null);
  const { download, isLoading } = useDownloadFile<number>({
    apiDefinition: (key, value) => {
      const def = options.find((o) => o.name === key)?.apiDefinition;
      if (!def || value === undefined) {
        return Promise.reject(new Error(`No apiDefinition found for key: ${key}`));
      }
      return def(value);
    },
    onError: (error) => {
      onError(error);
    },
    filename: (_key, value) => filename(value!),
  });
  const icon = startIcon ?? <PdfIcon />;

  return (
    <>
      {options.length === 1 ? (
        <SingleOptionDownloadButton
          loading={isLoading}
          icon={icon}
          onDownload={async () => setOptionToDownload(options[0].name)}
        >
          {buttonText}
        </SingleOptionDownloadButton>
      ) : (
        <MultiOptionDownloadButton
          loading={isLoading}
          options={options}
          icon={icon}
          onDownload={async (v) => setOptionToDownload(v)}
        >
          {buttonText}
        </MultiOptionDownloadButton>
      )}
      <YearDialog
        open={!!optionToDownload}
        maxYear={useDynamicPrices ? 2025 : undefined}
        onSelect={async (year) => {
          await download(optionToDownload ?? undefined, year);
          setOptionToDownload(null);
        }}
        onCancel={() => setOptionToDownload(null)}
      />
    </>
  );
};

export default DownloadPdfButtonWithYear;
